import React, { useState } from 'react';
import { ItineraryTypes } from '../types';

import { ItineraryOverviewItem } from './ItineraryOverviewItem';
import countriesRawData from '../../../config/data/countries.json';
import { AccordionStackable } from 'ui/AccordionStackable/AccordionStackable';
import { formatDateDisplay } from 'utils';
import { SvgIcon } from 'ui/SvgIcon';
import CalendarToday from 'ui/Icons/calendar-today.component.svg';
import { TFilterOption } from './ItineraryOverviewFilter';

export interface IItineraryOverviewGroupProps {
  destinationMarker?: ItineraryTypes['ItineraryItem'];
  items: ItineraryTypes['ItineraryItem'][];
  startDay?: number;
  endDay?: number;
  isOpenByDefault?: boolean;
  filter?: TFilterOption;
  customTitle?: string;
}

export const ItineraryOverviewGroup = ({
  destinationMarker,
  items,
  startDay,
  endDay,
  isOpenByDefault = false,
  filter = 'FULL_OVERVIEW',
  customTitle,
}: IItineraryOverviewGroupProps) => {
  const [isOpen, setIsOpen] = useState(isOpenByDefault);

  const country = destinationMarker?.countryCode ? countriesRawData[destinationMarker.countryCode] : undefined;

  const dayRangeText =
    startDay && endDay ? (startDay === endDay ? `DAY ${startDay}` : `DAY ${startDay}-${endDay}`) : '';

  const destinationText = destinationMarker ? `${destinationMarker.title}${country ? `, ${country.name}` : ''}` : '';

  // Apply filter to items
  const filterItem = (item: ItineraryTypes['ItineraryItem']) => {
    // Always exclude destination markers
    if (item.itemType === 'destinationMarker') return false;

    // Apply filter based on selection
    if (filter === 'FULL_OVERVIEW') {
      // For full overview, exclude accommodation and hotel markers (they'll be shown as synthetic markers)
      return item.itemType !== 'accommodation' && item.itemType !== 'hotelMarker';
    }
    if (filter === 'STAYS') {
      // For stays, we'll show synthetic hotel markers (created below)
      return false;
    }
    if (filter === 'TRANSFERS') return item.itemType === 'transfer';
    if (filter === 'GROUND_SERVICES') return item.itemType === 'groundService';
    if (filter === 'EXPERIENCES') return item.itemType === 'bespoke';

    return false;
  };

  // Filter out items based on current filter
  const itemsToDisplay = items.filter(filterItem);

  // Group accommodations by check-in date and hotelUuid to create synthetic hotel markers
  // Only do this if we have a destination marker (scheduled items)
  const accommodations = destinationMarker ? items.filter(item => item.itemType === 'accommodation') : [];
  const hotelGroups: { [key: string]: ItineraryTypes['ItineraryItem'][] } = {};

  accommodations.forEach(accommodation => {
    if (accommodation.date && accommodation.hotelUuid) {
      const key = `${accommodation.date}_${accommodation.hotelUuid}`;
      if (!hotelGroups[key]) {
        hotelGroups[key] = [];
      }
      hotelGroups[key].push(accommodation);
    }
  });

  // Create synthetic hotel markers for each group
  const syntheticHotelMarkers: ItineraryTypes['ItineraryItem'][] = [];
  const syntheticAccommodationsMap = new Map<string, ItineraryTypes['ItineraryItem'][]>();

  Object.entries(hotelGroups).forEach(([key, groupAccommodations]) => {
    const firstAccommodation = groupAccommodations[0];

    // Find the original hotel marker to get hotel details
    const originalHotelMarker = items.find(
      item => item.itemType === 'hotelMarker' && item.hotelUuid === firstAccommodation.hotelUuid
    );

    if (originalHotelMarker) {
      // Calculate the end date as the latest endDate from accommodations
      let latestEndDate = firstAccommodation.endDate || (firstAccommodation.date as string);
      groupAccommodations.forEach(acc => {
        if (acc.endDate && acc.endDate > latestEndDate) {
          latestEndDate = acc.endDate;
        }
      });

      // Create synthetic hotel marker
      const syntheticId = `synthetic_${key}`;
      const syntheticMarker: ItineraryTypes['ItineraryItem'] = {
        ...originalHotelMarker,
        uuid: syntheticId, // Unique ID for the synthetic marker
        date: firstAccommodation.date,
        endDate: latestEndDate,
        position: firstAccommodation.position, // Inherit position from first accommodation
      };
      syntheticHotelMarkers.push(syntheticMarker);

      // Store the accommodations for this synthetic marker
      syntheticAccommodationsMap.set(syntheticId, groupAccommodations);
    }
  });

  // Combine regular items with synthetic hotel markers (only if showing stays or full overview)
  const allItemsToDisplay =
    filter === 'FULL_OVERVIEW' || filter === 'STAYS' ? [...itemsToDisplay, ...syntheticHotelMarkers] : itemsToDisplay;

  // Sort function for items within a date group
  const sortItems = (a: ItineraryTypes['ItineraryItem'], b: ItineraryTypes['ItineraryItem']) => {
    // Priority 1: Items with position (sorted numerically)
    const aHasPosition = a.position !== undefined && a.position !== null;
    const bHasPosition = b.position !== undefined && b.position !== null;

    if (aHasPosition && bHasPosition) {
      return (a.position || 0) - (b.position || 0);
    }
    if (aHasPosition && !bHasPosition) return -1;
    if (!aHasPosition && bHasPosition) return 1;

    // Priority 2: Items with time (but no position)
    const aHasTime = !!a.time;
    const bHasTime = !!b.time;

    if (aHasTime && bHasTime) {
      // Both have times, compare them chronologically (HH:MM format sorts correctly as strings)
      return a.time!.localeCompare(b.time!);
    }
    if (aHasTime && !bHasTime) return -1;
    if (!aHasTime && bHasTime) return 1;

    // Priority 3: Items by type order (hotelMarker → transfer → groundService → bespoke)
    const typeOrder = {
      hotelMarker: 1,
      transfer: 2,
      groundService: 3,
      bespoke: 4,
    };

    const aTypeOrder = typeOrder[a.itemType as keyof typeof typeOrder] || 999;
    const bTypeOrder = typeOrder[b.itemType as keyof typeof typeOrder] || 999;

    return aTypeOrder - bTypeOrder;
  };

  // Don't render the group if there are no items to display after filtering
  if (allItemsToDisplay.length === 0) {
    return null;
  }

  return (
    <AccordionStackable
      isOpen={isOpen}
      onToggle={setIsOpen}
      wrapperClassName="mb-0 o:!border-none mb-5px last:mb-0"
      buttonClassName="py-15px o:bg-gray-10 o:pl-[5px]"
      contentWrapperClassName="px-0 py-0 o:pl-[5px]"
      summary={
        <div className="flex flex-row items-center w-full">
          {customTitle ? (
            <span className="font-hurmegeometric-sans text-14px text-flint uppercase font-bold">{customTitle}</span>
          ) : (
            <>
              <span className="font-hurmegeometric-sans text-14px text-flint uppercase font-bold min-w-[75px]">
                {dayRangeText}
              </span>
              <span className="font-hurmegeometric-sans text-14px text-flint uppercase">{destinationText}</span>
            </>
          )}
        </div>
      }
    >
      <div className="">
        {(() => {
          // For unscheduled items (no destination marker), don't group by date
          if (!destinationMarker) {
            const sortedItems = allItemsToDisplay.sort(sortItems);

            return (
              <div>
                {sortedItems.map((item, index) => (
                  <ItineraryOverviewItem
                    key={item.uuid}
                    item={item}
                    isLast={index === sortedItems.length - 1}
                    allItems={items}
                  />
                ))}
              </div>
            );
          }

          // Group items by date (for scheduled items)
          const itemsByDate: { [date: string]: ItineraryTypes['ItineraryItem'][] } = {};
          allItemsToDisplay.forEach(item => {
            const date = item.date || '';
            if (!itemsByDate[date]) {
              itemsByDate[date] = [];
            }
            itemsByDate[date].push(item);
          });

          // Sort dates
          const sortedDates = Object.keys(itemsByDate).sort();
          let totalRenderedItems = 0;

          return sortedDates.map(date => {
            const itemsForDate = itemsByDate[date];
            const sortedItemsForDate = itemsForDate.sort(sortItems);
            const dateDisplay = date ? formatDateDisplay(date) : 'Unscheduled';

            // Skip rendering this date group if it has no items
            if (sortedItemsForDate.length === 0) {
              return null;
            }

            return (
              <div key={date || 'unscheduled'} className="pb-20px">
                {/* the little date header separators */}
                <span className="font-hurmegeometric-sans text-12px text-brown-prime font-semibold uppercase mb-10px mt-20px first:mt-0 flex items-center gap-5px">
                  <SvgIcon IconComponent={CalendarToday} className="w-[15px] fill-brown-prime" />
                  <span>{dateDisplay}</span>
                </span>

                {/* Items for this date */}
                {sortedItemsForDate.map((item, index) => {
                  const isLastInDateGroup = index === sortedItemsForDate.length - 1;
                  const isLastOverall = totalRenderedItems === allItemsToDisplay.length - 1;
                  const isLastItem = isLastInDateGroup || isLastOverall;
                  totalRenderedItems++;
                  return (
                    <ItineraryOverviewItem
                      key={item.uuid}
                      item={item}
                      isLast={isLastItem}
                      allItems={items}
                      syntheticAccommodations={syntheticAccommodationsMap.get(item.uuid)}
                    />
                  );
                })}
              </div>
            );
          });
        })()}
      </div>
    </AccordionStackable>
  );
};
